import type { AssetContentType } from "../api";

const DEFAULT_WEBP_QUALITY = 0.75;
/** Hard cap for any uploaded asset. */
export const DEFAULT_MAX_ASSET_BYTES = 500 * 1024;

let webpSupport: boolean | null = null;

function supportsWebpEncoding(): boolean {
  if (webpSupport !== null) return webpSupport;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    webpSupport = canvas.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    webpSupport = false;
  }
  return webpSupport;
}

/** Returns the asset extension/content-type the browser can actually encode. */
export function webpOrPng(): { ext: "webp" | "png"; contentType: AssetContentType } {
  return supportsWebpEncoding()
    ? { ext: "webp", contentType: "image/webp" }
    : { ext: "png", contentType: "image/png" };
}

export function canvasToWebpBlob(
  canvas: HTMLCanvasElement,
  quality = DEFAULT_WEBP_QUALITY,
): Promise<Blob> {
  const { contentType } = webpOrPng();
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Failed to convert canvas to WebP"));
      },
      contentType,
      quality,
    );
  });
}

function rescaleCanvas(source: HTMLCanvasElement, scale: number): HTMLCanvasElement {
  const w = Math.max(1, Math.round(source.width * scale));
  const h = Math.max(1, Math.round(source.height * scale));
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  if (ctx) {
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(source, 0, 0, w, h);
  }
  return c;
}

/**
 * Encodes a canvas as WebP keeping it under `maxBytes`: first lowers quality, then,
 * if still too large, downscales the dimensions. Returns the smallest blob achieved.
 */
export async function canvasToWebpBlobCapped(
  canvas: HTMLCanvasElement,
  maxBytes = DEFAULT_MAX_ASSET_BYTES,
  startQuality = DEFAULT_WEBP_QUALITY,
): Promise<Blob> {
  let best = await canvasToWebpBlob(canvas, startQuality);
  if (best.size <= maxBytes) return best;

  for (let q = startQuality - 0.1; q >= 0.4; q -= 0.1) {
    const blob = await canvasToWebpBlob(canvas, q);
    if (blob.size < best.size) best = blob;
    if (best.size <= maxBytes) return best;
  }

  let scale = 1;
  while (best.size > maxBytes && Math.max(canvas.width, canvas.height) * scale > 600) {
    scale *= 0.8;
    const blob = await canvasToWebpBlob(rescaleCanvas(canvas, scale), 0.6);
    if (blob.size < best.size) best = blob;
  }
  return best;
}

/** Re-encodes an arbitrary image file as WebP to save storage. Falls back to the original on failure. */
export async function fileToWebpBlob(
  file: File,
  quality = DEFAULT_WEBP_QUALITY,
): Promise<Blob> {
  if (!supportsWebpEncoding()) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    return await canvasToWebpBlob(canvas, quality);
  } catch {
    return file;
  }
}

/** Like {@link fileToWebpBlob} but keeps the result under `maxBytes`. */
export async function fileToWebpBlobCapped(
  file: File,
  maxBytes = DEFAULT_MAX_ASSET_BYTES,
): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    return await canvasToWebpBlobCapped(canvas, maxBytes);
  } catch {
    return file;
  }
}
